<template>
  <DSDialog
    :open="isOpen"
    @update:open="handleClose"
  >
    <DSDialogContent class="max-w-ds-dialog-md">
      <template v-if="isLoading">
        <DSDialogHeader class="min-w-0">
          <DSDialogTitle>Group accounts</DSDialogTitle>
          <DSDialogDescription class="sr-only">
            Combine the selected items into one group.
          </DSDialogDescription>
        </DSDialogHeader>
        <div class="flex min-w-0 items-center justify-center gap-2 px-6 pb-8 text-sm font-medium text-muted-foreground">
          <Loader2 class="size-4 animate-spin motion-reduce:animate-none" />
          Loading…
        </div>
      </template>

      <template v-else-if="excludedAssets.length">
        <DSDialogHeader class="min-w-0">
          <DSDialogTitle>Group accounts</DSDialogTitle>
          <DSDialogDescription>
            Combine the selected items into one group. Their balances roll up under the owner you choose.
          </DSDialogDescription>
        </DSDialogHeader>
        <div class="min-w-0 px-6 pb-6">
          <p class="text-sm font-medium leading-normal text-muted-foreground">
            {{ excludedAssetsSentence }}
          </p>
        </div>
        <DSDialogFooter class="min-w-0">
          <DSButton
            type="button"
            variant="secondary"
            @click="handleClose"
          >
            Close
          </DSButton>
        </DSDialogFooter>
      </template>

      <form
        v-else
        class="contents"
        @submit.prevent="handlePrimaryAction"
      >
        <DSDialogHeader class="min-w-0">
          <DSDialogTitle>Group accounts</DSDialogTitle>
          <DSDialogDescription>
            Combine the selected items into one group. Their balances roll up under the owner you choose.
          </DSDialogDescription>
        </DSDialogHeader>

        <div class="flex min-w-0 flex-col gap-6 overflow-hidden px-6 pb-6">
          <div class="flex flex-col gap-2">
            <div class="flex items-baseline justify-between">
              <DSFieldLabel label="Selected" />
              <span class="text-xs font-medium text-muted-foreground">
                {{ assetsList.length }} {{ assetsList.length === 1 ? 'item' : 'items' }}
              </span>
            </div>
            <ul class="flex flex-col divide-y divide-border overflow-hidden rounded-ds-lg border border-border">
              <li
                v-for="asset in assetsList"
                :key="asset?.id"
                class="flex items-center gap-3 px-4 py-2.5"
              >
                <p class="truncate text-sm font-bold text-foreground">
                  {{ nameFor(asset) }}
                </p>
                <p
                  v-if="tickerFor(asset)"
                  class="truncate text-xs font-medium text-muted-foreground"
                >
                  {{ tickerFor(asset) }}
                </p>
              </li>
            </ul>
          </div>

          <div class="flex flex-col gap-3">
            <DSFieldLabel
              :id="ownerLabelId"
              label="Owner"
            />
            <DSRadioGroup
              v-model="mode"
              class="grid-cols-1 gap-2.5 sm:grid-cols-2"
              :aria-labelledby="ownerLabelId"
            >
              <DSChoiceCard
                value="existing"
                :icon="Users"
                title="Use existing"
                description="Pick a person or entity already in this repository."
              />
              <DSChoiceCard
                value="new"
                :icon="UserPlus"
                title="Create new"
                description="Name a new owner and choose its type."
              />
            </DSRadioGroup>

            <div
              v-if="mode === 'existing'"
              class="flex flex-col gap-1.5"
            >
              <DSFieldLabel
                :for="ownerFieldId"
                label="Existing owner"
                mark="required"
              />
              <AccountPicker
                :id="ownerFieldId"
                v-model="ownerId"
                :accounts
                :suggestion="ownerSuggestion"
                :disabled="isLoadingAction"
                placeholder="Find or create new account"
                empty-label="Nothing to pick — create a new owner instead."
              />
              <p
                v-if="ownerSuggestion"
                class="text-xs font-medium leading-normal text-muted-foreground"
              >
                {{ ownerSuggestion.reason }} Change it if it isn't right.
              </p>
            </div>

            <div
              v-else
              class="grid grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <div class="flex flex-col gap-1.5">
                <DSFieldLabel
                  :for="ownerNameFieldId"
                  label="Owner name"
                  mark="required"
                />
                <DSInput
                  :id="ownerNameFieldId"
                  v-model="ownerName"
                  :disabled="isLoadingAction"
                  placeholder="e.g. Mann Family Trust"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <DSFieldLabel
                  :for="ownerTypeFieldId"
                  label="Owner type"
                  mark="required"
                />
                <DSSelect
                  v-model="ownerType"
                  :disabled="isLoadingAction"
                >
                  <DSSelectTrigger
                    :id="ownerTypeFieldId"
                    class="w-full"
                  >
                    <DSSelectValue />
                  </DSSelectTrigger>
                  <DSSelectContent>
                    <DSSelectItem
                      v-for="type in accountTypes"
                      :key="type"
                      :value="type"
                    >
                      {{ type }}
                    </DSSelectItem>
                  </DSSelectContent>
                </DSSelect>
              </div>
            </div>
          </div>
        </div>

        <DSDialogFooter>
          <p
            v-if="requiredNoticeText"
            class="self-center text-xs font-medium text-muted-foreground sm:mr-auto"
          >
            {{ requiredNoticeText }}
          </p>
          <DSButton
            type="button"
            variant="secondary"
            :disabled="isLoadingAction"
            @click="handleClose"
          >
            Cancel
          </DSButton>
          <DSButton
            type="submit"
            :disabled="isLoadingAction || !canSubmit"
          >
            <Loader2
              v-if="isLoadingAction"
              class="size-4 animate-spin motion-reduce:animate-none"
            />
            {{ mode === 'existing' ? 'Group accounts' : 'Create and group' }}
          </DSButton>
        </DSDialogFooter>
      </form>
    </DSDialogContent>
  </DSDialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useId, watch } from 'vue'
import { Loader2, UserPlus, Users } from '@lucide/vue'

import { ModalClass } from '@types'

import { NOTIFICATION_DELAY } from '@/const'
import { ASSET_FIELD } from '@/entities/assets/utils/const'
import { AccountOption, AccountSuggestion } from '@/entities/assets/utils/types'
import { AssetClass } from '@/entities/assets'

import { handleCatchedError } from '@/helpers/common'
import { getCurrentDate } from '@/helpers/dates'

import { getAssetById } from '@/views/Analytics/utils/helpers'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useAssetsStore } from '@/store/assets'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import AccountPicker from '@/entities/assets/components/AccountPicker.vue'

import {
  DSButton,
  DSChoiceCard,
  DSDialog,
  DSDialogContent,
  DSDialogDescription,
  DSDialogFooter,
  DSDialogHeader,
  DSDialogTitle,
  DSFieldLabel,
  DSInput,
  DSRadioGroup,
  DSSelect,
  DSSelectContent,
  DSSelectItem,
  DSSelectTrigger,
  DSSelectValue,
  DSToast,
} from '@ds'

const GROUPING_TRANSACTION_TYPE = 'add account'
const GROUPING_ASSET_TYPE = 'grouping account'

type OwnerMode = 'existing' | 'new'

type Props = {
  ids: string[]
  modal: ModalClass<any>
}

type Emits = {
  complete: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const assetsStore = useAssetsStore()
const transactionsSettingsStore = useTransactionsSettingsStore()
const transactionsStore = useTransactionsStore()

const ownerLabelId = useId()
const ownerFieldId = useId()
const ownerNameFieldId = useId()
const ownerTypeFieldId = useId()

const isOpen = computed(() => props.modal.isOpened)

const defaultDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

const isLoading = computed(() => !transactionsSettingsStore.initFlag)

const isLoadingAction = computed(
  () => transactionsStore.loadingAction || assetsStore.loadingAction,
)

const settings = computed(
  () =>
    transactionsSettingsStore.getList.find(
      item => item.name === GROUPING_TRANSACTION_TYPE,
    )?.entries?.[0],
)

const instanceList = computed(() =>
  props.ids.map(id => getAssetById(id, assetsBunchStore.getList)),
)

const assetsList = computed(() =>
  instanceList.value.filter(
    (asset): asset is AssetClass =>
      asset !== undefined &&
      !!settings.value?.asset?.includes?.includes(
        asset.field<string>(ASSET_FIELD.TYPE).value || '',
      ),
  ),
)

const excludedAssets = computed(() =>
  instanceList.value
    .filter(
      asset =>
        !settings.value?.asset?.includes?.includes(
          asset?.field<string>(ASSET_FIELD.TYPE).value || '',
        ),
    )
    .map(asset => asset?.field<string>(ASSET_FIELD.NAME).value),
)

const excludedAssetsSentence = computed(() => {
  const label = excludedAssets.value.length > 1 ? 'Assets' : 'Asset'
  return `${label} ${excludedAssets.value.join(', ')} cannot be grouped. Only accounts can be grouped.`
})

const nameFor = (asset: AssetClass) => asset.field<string>(ASSET_FIELD.NAME).value

const tickerFor = (asset: AssetClass) => {
  const name = asset.field<string>(ASSET_FIELD.NAME).value
  const ticker = asset.field<string>(ASSET_FIELD.TICKER).value
  return ticker && ticker !== name ? ticker : ''
}

/*
 * Allowed owner/account types for this transaction. 
 * Matches backend constraint for the owner picker—prevents creating owners of invalid types.
 */
const accountTypes = computed(() => settings.value?.account?.includes ?? [])

const accounts = computed<AccountOption[]>(() => {
  if (!accountTypes.value.length) return []
  return Array.from(assetsBunchStore.getList.values())
    .filter(instance => !props.ids.includes(instance.id))
    .map(instance => instance.get())
    .filter(asset => accountTypes.value.includes(asset.type))
    .map(asset => ({ id: asset.id, name: asset.name, ticker: asset.ticker, type: asset.type }))
})

// Suggests a common owner if all selected items share one and tree is loaded; otherwise, no suggestion.
const ownerSuggestion = computed<AccountSuggestion | undefined>(() => {
  const treeList = analyticsStore.module.tree.getList
  if (!analyticsStore.module.tree.isInit || !treeList || !assetsList.value.length) return undefined

  const ownerIds = new Set<string>()
  for (const asset of assetsList.value) {
    const row = treeList.find(item => item.path.at(-1) === asset.id)
    const ownerId = row && row.path.length > 2 ? row.path.at(-2) : undefined
    if (!ownerId) return undefined
    ownerIds.add(ownerId)
  }
  if (ownerIds.size !== 1) return undefined

  const [ownerId] = ownerIds
  if (!accounts.value.some(account => account.id === ownerId)) return undefined

  return { accountId: ownerId, reason: 'Current owner of the selected items.' }
})

const mode = ref<OwnerMode>('existing')
const ownerId = ref('')
const ownerName = ref('')
const ownerType = ref('')

const canSubmit = computed(() => {
  if (mode.value === 'existing') return !!ownerId.value
  return !!ownerName.value.trim() && !!ownerType.value
})

const requiredNoticeText = computed(() => {
  if (canSubmit.value) return ''
  return mode.value === 'existing' ? 'Choose an owner to continue.' : 'Add an owner name to continue.'
})

// Default the type only once a type hasn't been chosen yet, so toggling back
// and forth between the two modes never throws away what the user picked.
watch(mode, next => {
  if (next === 'new' && !ownerType.value) {
    ownerType.value = accountTypes.value.includes(GROUPING_ASSET_TYPE)
      ? GROUPING_ASSET_TYPE
      : accountTypes.value[0] ?? ''
  }
})

const handleClose = () => {
  props.modal.close()
}

const handlePrimaryAction = async () => {
  if (!canSubmit.value || isLoadingAction.value) return

  let ownerAssetId = ownerId.value
  if (mode.value === 'new') {
    const instance = assetsBunchStore.createElement()
    instance.set({
      type: ownerType.value,
      name: ownerName.value.trim(),
      ticker: ownerName.value.trim(),
    })
    try {
      await instance.store()
    } catch (e) {
      handleCatchedError(e as string)
      return
    }
    if (!instance.id) return
    ownerAssetId = instance.id
  }

  const transactions = assetsList.value.map(asset => ({
    date: defaultDate.value,
    type: GROUPING_TRANSACTION_TYPE,
    entries: [
      {
        account_id: ownerAssetId,
        amount: 1,
        asset_id: `${asset.id}`,
      },
    ],
    description: '',
  }))
  const nid = DSToast.loading('Grouping assets', {
    position: 'top-right',
  })
  try {
    await transactionsStore.bulkStore(transactions)
    DSToast.success('Assets are grouped', { id: nid, duration: NOTIFICATION_DELAY, position: 'top-right' })
    transactionsStore.clear()
    // Grouping changes the ownership tree, so every active analytics view
    // (not just the tree) needs to be refetched rather than read stale.
    analyticsStore.markAsDeprecated()
  } catch (e) {
    DSToast.dismiss(nid)
    handleCatchedError(e as string)
    return
  }
  handleClose()
  emit('complete')
}

watch(isOpen, open => {
  if (open) return
  mode.value = 'existing'
  ownerId.value = ''
  ownerName.value = ''
  ownerType.value = ''
})

onMounted(() => {
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch()
  }
})
</script>
